// Only explicit server-authored card data selects a compact write surface.
import { z } from 'zod';

export const CANCELLED_BY = ['office', 'caregiver', 'client'] as const;

export const compactCardSchema = z
    .object({
        kind: z.literal('compact'),
        // Cards persisted before this release carry no client name.
        client_name: z.string().min(1).optional(),
        summary: z.string().min(1),
        completed_summary: z.string().min(1),
        // Absent means saved; only a vendor refusal sends false.
        saved: z.boolean().optional(),
    })
    .strict();

// Assignments were their own card until this release. Approval events are
// durable, so old ones still arrive and are read into the compact shape
// rather than dropped: a card that fails to parse is skipped, which would
// silently erase the approval from a past conversation.
const legacyAssignmentCardSchema = z
    .object({
        kind: z.literal('assignment'),
        caregiver_name: z.string().min(1),
        shift_id: z.uuid(),
        client_name: z.string().min(1),
        interval: z.string().min(1),
    })
    .strict()
    .transform((card): z.infer<typeof compactCardSchema> => ({
        kind: 'compact' as const,
        client_name: card.client_name,
        summary: `Assign ${card.caregiver_name} to ${card.client_name} on ${card.interval}?`,
        completed_summary: `Assigned ${card.caregiver_name} to ${card.client_name}`,
    }));

export const shiftWriteCardSchema = z.union([
    compactCardSchema,
    legacyAssignmentCardSchema,
]);

// Which rates the card must ask for. The options are never carried here:
// the card fetches them, so a client with hundreds cannot bloat the event.
export const ehrRatePickerSchema = z
    .object({
        target: z.string().min(1),
        // A create has no visit to key rates on, so it carries the client
        // and the window it is about to book instead. The three default to
        // null so a card event from the release before this one still parses.
        shift_id: z.string().min(1).nullable(),
        client_id: z.string().min(1).nullable().default(null),
        starts_at: z.string().min(1).nullable().default(null),
        ends_at: z.string().min(1).nullable().default(null),
        caregiver_id: z.string().min(1),
        known_client_rate_id: z.string().min(1).nullable(),
        rates: z
            .array(
                z
                    .object({
                        key: z.enum(['client', 'caregiver']),
                        label: z.string().min(1),
                        allow_custom_amount: z.boolean(),
                    })
                    .strict(),
            )
            .min(1),
    })
    .strict();

// WellSky names a warning only when it refuses, so the card asks about
// exactly the ones this write hit.
export const ehrOverrideEditorSchema = z
    .object({
        target: z.string().min(1),
        warnings: z
            .array(
                z
                    .object({
                        key: z.string().min(1),
                        label: z.string().min(1),
                        consequence: z.string().min(1),
                    })
                    .strict(),
            )
            .min(1),
    })
    .strict();

export const shiftWriteEditorSchema = z
    .object({
        kind: z.literal('shift_write'),
        card: shiftWriteCardSchema,
        details: z.array(
            z
                .object({
                    heading: z.string(),
                    items: z.array(z.string()),
                })
                .strict(),
        ),
        notices: z.array(z.string()),
        rate_picker: ehrRatePickerSchema.optional(),
        override_editor: ehrOverrideEditorSchema.optional(),
        // A WellSky cancel tags its note; options come from the shift.
        tag_picker: z
            .object({
                shift_id: z.string().min(1),
                tag_ids: z.array(z.string().min(1)),
            })
            .strict()
            .optional(),
        // A cancel's note, prefilled from the chat and editable on the card.
        cancel_note: z
            .object({
                note: z.string().min(1),
                cancelled_by: z.enum(CANCELLED_BY),
                cancelled_by_options: z.array(z.enum(CANCELLED_BY)).min(1),
            })
            .strict()
            .optional(),
    })
    .strict();

export type EhrRatePicker = z.infer<typeof ehrRatePickerSchema>;
export type EhrOverrideEditor = z.infer<typeof ehrOverrideEditorSchema>;
export type ShiftWriteCard = z.infer<typeof shiftWriteCardSchema>;
export type ShiftWriteEditor = z.infer<typeof shiftWriteEditorSchema>;
